import { useEffect, useId } from "react";
import * as RadioGroup from "@radix-ui/react-radio-group";
import type { Question } from "../../lib/survey-config";
import type { QuestionProps } from "./types";
import { QuestionHeader } from "./QuestionHeader";

type SingleSelectQuestion = Extract<Question, { type: "singleSelect" }>;

function isValid(q: SingleSelectQuestion, v: string): boolean {
  if (q.required === false) return true;
  return q.options.some((o) => o.value === v);
}

export function SingleSelectQuestion({
  question,
  value,
  onChange,
  onValid,
  autoFocus,
}: QuestionProps<SingleSelectQuestion, string>) {
  const baseId = useId();

  useEffect(() => {
    onValid(isValid(question, value));
  }, [question, value, onValid]);

  return (
    <div className="space-y-4">
      <QuestionHeader
        title={question.title}
        subtitle={question.subtitle}
        required={question.required !== false}
      />
      <RadioGroup.Root
        value={value}
        onValueChange={onChange}
        className="space-y-2"
        aria-label={question.title}
      >
        {question.options.map((opt, idx) => {
          const id = `${baseId}-${opt.value}`;
          return (
            <label
              key={opt.value}
              htmlFor={id}
              className="flex cursor-pointer items-center gap-3 rounded-input border border-border bg-bg-elevated px-4 py-3 transition hover:border-accent has-[:checked]:border-accent has-[:checked]:bg-accent/5"
            >
              <RadioGroup.Item
                id={id}
                value={opt.value}
                autoFocus={autoFocus && idx === 0}
                className="flex h-5 w-5 items-center justify-center rounded-full border-2 border-border data-[state=checked]:border-accent"
              >
                <RadioGroup.Indicator className="block h-2.5 w-2.5 rounded-full bg-accent" />
              </RadioGroup.Item>
              <span className="text-sm leading-snug">{opt.label}</span>
            </label>
          );
        })}
      </RadioGroup.Root>
    </div>
  );
}
